import React from 'react';
import { Modal } from '@milanote/roma';
import { cx } from '@milanote/roma/utils';

import Icon from '../../components/icons/Icon';
import { ModalIds } from '../../components/modal/modalConstants';
import useActiveModal from '../../components/modal/useActiveModal';
import useRouteModalOpenState from '../../components/modal/useRouteModalOpenState';
import { useEnsurePasswordManagersInteraction } from './hooks/useEnsurePasswordManagersInteraction';

import './RomaAuthModal.scss';

type RomaAuthModalHeaderProps = {
    title: string;
    subtitle?: string;
    className?: string;
};

type RomaAuthModalProps = {
    className?: string;
    overlay?: boolean;
    close: (() => void) | null;
};

type RomaAuthModalComponent = React.FC<RomaAuthModalProps> & {
    Header: React.FC<RomaAuthModalHeaderProps>;
};

const RomaAuthModalHeader: React.FC<RomaAuthModalHeaderProps> = ({ title, subtitle, className }) => {
    return (
        <div className={cx('flex flex-col items-center gap-3xl', className)}>
            <span className="shrink-0 text-primary">
                <Icon name="milanote-logo-medium" />
            </span>
            <div className="flex flex-col items-center gap-md w-full">
                <h1 className="text-center text-display-3xl font-serif text-primary">{title}</h1>
                {subtitle && <p className="text-center text-display-xs text-fg-tertiary font-normal">{subtitle}</p>}
            </div>
        </div>
    );
};

export const RomaAuthSheetContent: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
    children,
    className,
}) => {
    return <div className={cx('RomaAuthSheetContent w-full p-xl', className)}>{children}</div>;
};

const RomaAuthModal: RomaAuthModalComponent = ({ children, className, overlay, close }) => {
    useEnsurePasswordManagersInteraction();
    useActiveModal(overlay ? ModalIds.AUTH_IN_APP : ModalIds.AUTH);

    const closeRoute = React.useCallback(() => close?.(), [close]);
    const { isOpen, onOpenChange, onAnimationEnd } = useRouteModalOpenState(closeRoute);

    const backgroundStyle = overlay ? 'dim' : 'solid';
    const transitionStyle = overlay ? 'fade' : 'none';
    const isDismissable = Boolean(close);

    return (
        <Modal.Overlay
            backgroundStyle={backgroundStyle}
            isOpen={isOpen}
            transitionStyle={transitionStyle}
            onOpenChange={onOpenChange}
            onAnimationEnd={onAnimationEnd}
            isDismissable={isDismissable}
            isKeyboardDismissDisabled={!isDismissable}
            className="RomaAuthModalOverlay"
        >
            {/* Keeps the form at full size; the overlay scrolls it when a virtual keyboard leaves too little room */}
            <Modal className="max-w-110 max-h-[none]">
                <Modal.Dialog>
                    <Modal.DialogContent
                        closeButton={isDismissable}
                        className={cx('RomaAuthModal', 'max-w-110', className)}
                    >
                        {children}
                    </Modal.DialogContent>
                </Modal.Dialog>
            </Modal>
        </Modal.Overlay>
    );
};

RomaAuthModal.Header = RomaAuthModalHeader;

export default RomaAuthModal;
